<template>
  <div class="page-container">
    <div class="header">
      <h2>股票监控</h2>
    </div>
    
    <!-- 浮动报警显示 - 移至header之后，确保不影响页面布局 -->
    <div class="floating-alerts-container">
      <div 
        v-for="alert in floatingAlerts" 
        :key="alert.id"
        class="floating-alert"
        :class="alert.type"
      >
        <span class="alert-content">{{ alert.message }}</span>
        <button class="close-btn" @click="removeFloatingAlert(alert.id)">×</button>
      </div>
    </div>
    
    <div class="add-stock-form">
      <input type="text" v-model.trim="newStockCode" @keyup.enter="addStock" placeholder="输入股票代码 (e.g., 000123)">
      <button @click="addStock">添加监控</button>
    </div>
    
    <div class="stock-list">
      <!-- <h2>监控列表</h2> -->
      <div v-if="displayedStocks.length === 0" class="empty-state">
        暂无监控的股票，请在上方添加。
      </div>
      <div v-else class="stock-item" v-for="stock in displayedStocks" :key="stock.code">
        <!-- 调试信息：显示完整的stock对象 -->
        <div v-if="debugStock" style="background-color: #f0f0f0; padding: 10px; margin: 10px 0; border: 1px solid #ccc;">
          <strong>调试信息 - Stock对象:</strong>
          <pre>{{ JSON.stringify(stock, null, 2) }}</pre>
        </div>
        <div class="stock-title-container">
          <div class="stock-title-left" v-if="stock.currentData && stock.currentData && stock.currentData.success !== false">
            <h3><span v-if="stock.currentData.data.name"><a :href="$router.resolve({ name: 'HealthCheck', params: { stockCode: stock.code } }).href" target="_blank" class="stock-link">{{ stock.currentData.data.name }}</a></span> ({{ stock.code }})</h3>
            <div 
              class="stock-info" 
              :class="getValueColor(stock.currentData.data.changePercent)"
            >
              <span>{{ stock.currentData.data.changePercent }}</span>
              <span class="current-price"> {{ getCurrentPrice(stock.code, stock.currentData.data.price) }} </span>
              <span :class="getValueColor(stock.currentData.data.price - stock.currentData.data.lastPrice)"> {{ getCurrentPriceArrowIcon(stock.currentData.data.price, stock.currentData.data.lastPrice) }} </span>
            </div>
            <div :class="getValueColor(stock.currentData.data.todayChangePercent)">
              <span>&nbsp;(</span>
              <span :class="getValueColor(stock.currentData.data.dateChange)">{{ getCurrentPrice(stock.code, stock.currentData.data.openingPrice) }}: </span>
              <span>{{ stock.currentData.data.todayChangePercent }}</span>
              <span>)</span>
            </div>
          </div>
          <div class="stock-title-right" v-else>
            <h3>({{ stock.code }})</h3>
          </div>
          <div >
            <div class="ma-score" v-if="stock.score >= 0">
              <span>(</span>
              <span :class="getValueColor(stock.score, 50)">{{ stock.score }}</span>
               <span :class="getValueColor(stock.scoreUp)">{{ getDoubleArrowIcon(stock) }}</span>
              <span>)</span>
            </div>
          </div>
          <div class="stock-title-right">
            <button :class="getPinBtnClass(stock)" @click="togglePinStock(stock)">
              {{ stock.isPinned ? '取消' : '置顶' }}
            </button>
            <button class="delete-btn" @click="removeStock(stock.code)">×</button>
          </div>
        </div>
        <!-- 显示不同时间周期的数据 -->
        <div class="indicators-container">
          <div class="indicator-data" v-for="period in ['1', '5', '30', 'd', 'w']" :key="period" v-if="settings.selectedPeriods.includes(period) && stock.indicatorData && stock.indicatorData[period]">
            <div class="indicator-title">
              <span>{{ getScaleLabel(period) }}</span>
              <span>(</span>
              <span :class="getValueColor(stock.scaleScores && stock.scaleScores[period] ? stock.scaleScores[period].score : -1, 6)">{{ stock.scaleScores && stock.scaleScores[period] ? stock.scaleScores[period].score : '-' }}</span>
              <span :class="getValueColor(stock.scaleScores && stock.scaleScores[period] ? stock.scaleScores[period].scoreUp : 0)">{{ getDoubleArrowIcon(stock.scaleScores && stock.scaleScores[period] ? stock.scaleScores[period] : { scoreUp: 0 }) }}</span>
              <span>)：</span>
            </div>
            <div class="kvalue-container">
              <div>
                <span class="item-title">K: </span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.k.positive)">{{ stock.indicatorData[period].indicators2.k.value.toFixed(stock.indicatorData[period].precision) }}</span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.k.up)"> {{ getArrowIcon(stock.indicatorData[period].indicators2.k.up) }}</span>
              </div>
              <div>
                <span class="item-title">MA5: </span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.ma5.positive)">{{ stock.indicatorData[period].indicators2.ma5.value.toFixed(stock.indicatorData[period].precision) }}</span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.ma5.up)"> {{ getArrowIcon(stock.indicatorData[period].indicators2.ma5.up) }}</span>
              </div>
            </div>
            <div v-if="stock.indicatorData[period].macd" class="macd-container">
              <div>
                <span class="item-title">MACD: </span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.macd.positive)">{{ stock.indicatorData[period].indicators2.macd.value.toFixed(3) }}</span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.macd.up)"> {{ getArrowIcon(stock.indicatorData[period].indicators2.macd.up) }}</span>
              </div>
              <div>
                <span class="item-title">MA20: </span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.ma20.positive)">{{ stock.indicatorData[period].indicators2.ma20.value.toFixed(stock.indicatorData[period].precision) }}</span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.ma20.up)"> {{ getArrowIcon(stock.indicatorData[period].indicators2.ma20.up) }}</span>
              </div>
            </div>
            <div v-if="stock.indicatorData[period].kdj" class="kdj-container">
              <div>
                <span class="item-title">KDJ: </span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.kdj.positive)">{{ stock.indicatorData[period].indicators2.kdj.value.toFixed(2) }}</span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.kdj.up)"> {{ getArrowIcon(stock.indicatorData[period].indicators2.kdj.up) }}</span>
              </div>
              <div>
                <span class="item-title">MA60: </span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.ma60.positive)">{{ stock.indicatorData[period].indicators2.ma60.value.toFixed(stock.indicatorData[period].precision) }}</span>
                <span :class="getValueColor(stock.indicatorData[period].indicators2.ma60.up)"> {{ getArrowIcon(stock.indicatorData[period].indicators2.ma60.up) }}</span>
              </div>
            </div>
            <div v-if="stock.alerts && stock.alerts[period] && stock.alerts[period].length > 0" class="alerts-container">
              <span 
                v-for="(alert, index) in stock.alerts[period]" 
                :key="index" 
                :class="alert.color"
                :title="settings.showAlertDetails ? alert.time + ' ' + alert.event : ''"
              >
                {{ alert.abbr }}
              </span>
            </div>
          </div>
        </div>
        <div class="alerts-list" v-if="false">
          <h4>最近报警事件：</h4>
          <div v-for="(alerts, scale) in stock.alerts" :key="scale" v-show="alerts.length > 0">
            <h5>{{ getScaleLabel(scale) }}:</h5>
            <ul>
              <li v-for="(alert, index) in alerts" :key="index">
                <span>{{ alert.event }}</span>
                <span class="alert-time">{{ alert.time }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../assets/common-styles.css';
import mockApi from '../api/mock';
import { getMarketParam, uniformStockCode } from '../api/mock';
import * as TA from 'technicalindicators';
import { getPricePrecision } from '@/utils/utils';
import { isValidStockCode } from '@/utils/stockUtils';

export default {
  name: 'Monitor',
  data() {
    return {
      newStockCode: '',
      // monitoredStocks 的结构: [{ code: 'AAPL', alerts: { '1': [{ event: '...', time: '...' }], '5': [{ event: '...', time: '...' }], ... } }]
      monitoredStocks: [], 
      pollingIntervals: [],
      debugStock: false,
      // 浮动报警信息数组
      floatingAlerts: [],
      // 用于跟踪报警ID的计数器
      alertIdCounter: 0,
      // 设置数据
      settings: {
        selectedPeriods: ['5', '30', 'd'], // 默认选中5分钟、30分钟、日线
        alerts: {
          '1': [],
          '5': [],
          '30': [],
          'd': [],
          'w': []
        },
        tdSequentialSettings: {
          '1': '9',
          '5': '9',
          '30': '9',
          'd': '9',
          'w': '9'
        },
        showAlertDetails: false // 默认不显示报警详情
      },
      overBuyWarning: [],
      overSellWarning: [],
      // 用于记录前一个路由
      previousRoute: null,
      // 定时更新监控列表的定时器
      localStorageUpdateInterval: null,
      // 存储上一次监控列表的哈希值，用于比较是否有变化
      lastMonitoredStocksHash: ''
    };
  },
  computed: {
    // 计算属性，用于获取当前显示的股票数据，并按置顶状态排序
    displayedStocks() {
      // 创建一个新的数组副本以避免修改原始数组
      const sortedStocks = [...this.monitoredStocks];
      return sortedStocks.sort((a, b) => {
        // 置顶的股票排在前面
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
      });
    }
  },
  created() {
    // 使用路由导航后的守卫来跟踪前一个路由
    this.$router.afterEach((to, from) => {
      if (to.name === 'Monitor') {
        this.previousRoute = from.name;
      }
    });
  },
  methods: {
    // 初始化监控页面
    initMonitor() {
      // 请求浏览器通知权限
      this.requestNotificationPermission();
      // 2a: 页面初始化时加载
      this.loadStocksFromLocalStorage();
      // 加载设置数据
      this.loadSettings();
      // this.cleanupExpiredAlerts();
      // 2b: 开始轮询
      this.startPolling();
    },
    // 从localStorage加载设置
    loadSettings() {
      const savedSettings = localStorage.getItem('stockMonitorSettings');
      if (savedSettings) {
        this.settings = {
          ...this.settings,
          ...JSON.parse(savedSettings)
        };
        
        // 确保所有时间周期都有对应的报警设置和神奇九转设置
        this.settings.selectedPeriods.forEach(period => {
          if (!this.settings.alerts[period]) {
            this.settings.alerts[period] = [];
          }
          if (!this.settings.tdSequentialSettings[period]) {
            this.settings.tdSequentialSettings[period] = '9';
          }
        });
        
        // 确保showAlertDetails属性存在，默认为false
        if (this.settings.showAlertDetails === undefined) {
          this.settings.showAlertDetails = false;
        }
      } else {
        // 如果localStorage中没有保存的数据，使用初始值
        this.settings = {
          selectedPeriods: ['1', '5', '30', 'd', 'w'], // 默认选中5分钟、30分钟、日线
          alerts: {
            '1': ['macdRed', 'kdjBearishDivergence', 'kdjBullishDivergence'],
            '5': ['macdRed', 'kdjBearishDivergence', 'kdjBullishDivergence', 'td6', 'td7', 'td8', 'td9'],
            '30': ['macdRed', 'kdjBearishDivergence', 'kdjBullishDivergence', 'td6', 'td7', 'td8', 'td9'],
            'd': ['macdRed', 'kdjBearishDivergence', 'kdjBullishDivergence', 'td6', 'td7', 'td8', 'td9'],
            'w': ['macdRed', 'kdjBearishDivergence', 'kdjBullishDivergence']
          },
          tdSequentialSettings: {
            '1': '9',
            '5': '9',
            '30': '9',
            'd': '9',
            'w': '9'
          },
          showAlertDetails: false // 默认不显示报警详情
        };
      }
      
      // 保存初始值到localStorage
      localStorage.setItem('stockMonitorSettings', JSON.stringify(this.settings));
    },

    // 删除股票
    removeStock(code) {
      if (confirm(`确定要从监控列表中删除股票 ${code} 吗？`)) {
        const index = this.monitoredStocks.findIndex(stock => stock.code === code);
        if (index !== -1) {
          this.monitoredStocks.splice(index, 1);
          this.saveStocksToLocalStorage();
          // 更新哈希值，避免重复刷新页面
          this.lastMonitoredStocksHash = this.getMonitoredStocksHash(this.monitoredStocks);
          
          // 删除localStorage中与这支股票相关的divergence_data
          this.removeDivergenceDataForStock(code);
        }
      }
    },
    // 2a: 添加股票
    addStock() {
      if (!this.newStockCode) {
        alert("股票代码不能为空！");
        return;
      }
      
      // 验证股票代码是否合法
      if (!isValidStockCode(this.newStockCode)) {
        alert("请输入合法的股票代码！\n\n支持格式：\n- 5位或6位纯数字 (如: 000001, 12345)\n- 带前缀的代码 (如: sz000001, sh600000, hk00700)");
        return;
      }
      
      const codeToAdd = uniformStockCode(this.newStockCode);
      // 在添加的时候就统一处理前缀
      if (!codeToAdd) {
        alert("不支持的股票代码：" + this.newStockCode);
        return;
      }
      if (this.monitoredStocks.some(stock => stock.code === codeToAdd)) {
        alert("该股票已在监控列表中！");
        this.newStockCode = '';
        return;
      }
      
      // 初始化alerts为按scale分组的结构
      const alertsByScale = {
        '1': [],
        '5': [],
        '30': [],
        'd': [],
        'w': []
      };
      const formattedData = {
        success: true,
        data: {
          name: '',
          price: 0,
          change: 0,
          todayChangePercent: '0.00%',
          changePercent: '0.00%',
          openingPrice: 0,
          highestPrice: 0,
          lowestPrice: 0,
          closeingPrice: 0,
          dateChange: 0,
          lastPrice: -1,
          lastScore: -1,
          score: -1,
        }
      };
      this.monitoredStocks.push({ code: codeToAdd, alerts: alertsByScale, macdColor: null, isPinned: false, currentData: formattedData });
      this.saveStocksToLocalStorage();
      // 更新哈希值，避免重复刷新页面
      this.lastMonitoredStocksHash = this.getMonitoredStocksHash(this.monitoredStocks);
      this.newStockCode = '';
      console.log("all stocks: ", this.monitoredStocks);
      
      // 添加后立即获取一次数据
      this.fetchAllData();
    },

    // 获取时间周期的标签
    getScaleLabel(scale) {
      const scaleLabels = {
        '1': '1F',
        '5': '5F',
        '30': '30F',
        'd': '日线',
        'w': '周线'
      };
      return scaleLabels[scale] || scale;
    },

    // 置顶/取消置顶股票
    togglePinStock(stock) {
      const index = this.monitoredStocks.findIndex(s => s.code === stock.code);
      if (index !== -1) {
        // 切换置顶状态
        stock.isPinned = !stock.isPinned;
        
        // 如果是置顶操作，将股票移动到数组最前面
        if (stock.isPinned) {
          // 从当前位置移除股票
          const [removedStock] = this.monitoredStocks.splice(index, 1);
          // 将股票添加到数组最前面
          this.monitoredStocks.unshift(removedStock);
        }
        
        this.saveStocksToLocalStorage();
      }
    },
    
    // 2a: 从 localStorage 加载股票列表
    // 计算监控列表的哈希值
    getMonitoredStocksHash(stocks) {
      // 如果没有监控的股票，返回空字符串
      if (!stocks || stocks.length === 0) {
        return '';
      }
      
      // 只取出每个item的code，形成新的数组，然后排序
      const stockCodes = stocks.map(stock => typeof stock === 'object' ? stock.code : stock).sort();
      
      // 将排序后的股票代码数组转换为字符串并返回其JSON表示
      return JSON.stringify(stockCodes);
    },
    
    // 检查localStorage中的监控列表是否有变化
    checkAndLoadStocksFromLocalStorage() {
      const stocks = localStorage.getItem('monitoredStocks');
      if (stocks) {
        const stockData = JSON.parse(stocks);
        // 计算当前localStorage中监控列表的哈希值
        const currentHash = this.getMonitoredStocksHash(stockData);
        
        // 只有在哈希值不同时才更新监控列表
        if (currentHash !== this.lastMonitoredStocksHash) {
          console.log("检测到监控股票列表有变化，正在更新...");
          
          // 清除当前的所有轮询定时器，避免在更新过程中产生冲突
          if (this.pollingIntervals && this.pollingIntervals.length > 0) {
            this.pollingIntervals.forEach(interval => {
              clearInterval(interval);
            });
          }
          
          // 兼容旧格式（只有股票代码数组）和新格式（包含isPinned属性的对象数组）
          if (Array.isArray(stockData) && stockData.length > 0) {
            if (typeof stockData[0] === 'string') {
              // 旧格式：只包含股票代码
              // 初始化alerts为按scale分组的结构
              const alertsByScale = {
                '1': [],
                '5': [],
                '30': [],
                'd': [],
                'w': []
              };
              this.monitoredStocks = stockData.map(code => ({ code, alerts: alertsByScale, isPinned: false }));
            } else {
              // 新格式：包含股票代码和isPinned属性
              this.monitoredStocks = stockData.map(item => ({ 
                code: item.code, 
                // 确保alerts是按scale分组的结构
                alerts: typeof item.alerts === 'object' && !Array.isArray(item.alerts) ? item.alerts : {
                  '1': [],
                  '5': [],
                  '30': [],
                  'd': [],
                  'w': []
                }, 
                isPinned: item.isPinned || false 
              }));
            }
          }
          
          console.log("更新监控股票列表:", this.monitoredStocks);
          this.cleanStockAlerts();
          
          // 重新启动轮询
          this.startPolling();
          
          // 更新lastMonitoredStocksHash为新的哈希值
          this.lastMonitoredStocksHash = currentHash;
        }
      }
    },
    
    loadStocksFromLocalStorage() {
      const stocks = localStorage.getItem('monitoredStocks');
      const alertsByScale = {
        '1': [],
        '5': [],
        '30': [],
        'd': [],
        'w': []
      };
      const formattedData = {
        success: true,
        data: {
          name: '',
          price: 0,
          change: 0,
          todayChangePercent: '0.00%',
          changePercent: '0.00%',
          openingPrice: 0,
          highestPrice: 0,
          lowestPrice: 0,
          closeingPrice: 0,
          dateChange: 0,
          lastPrice: -1,
          lastScore: -1,
          score: -1,
        }
      };
      if (stocks) {
        const stockData = JSON.parse(stocks);
        // 兼容旧格式（只有股票代码数组）和新格式（包含isPinned属性的对象数组）
        if (Array.isArray(stockData) && stockData.length > 0) {
          if (typeof stockData[0] === 'string') {
            this.monitoredStocks = stockData.map(code => ({ code, alerts: alertsByScale, isPinned: false, currentData: formattedData }));
          } else {
            // 新格式：包含股票代码和isPinned属性
            this.monitoredStocks = stockData.map(item => ({ 
              code: item.code, 
              // 确保alerts是按scale分组的结构
              alerts: typeof item.alerts === 'object' && !Array.isArray(item.alerts) ? item.alerts : alertsByScale, 
              isPinned: item.isPinned || false,
              currentData: formattedData
            }));
          }
        }
        console.log("load stocks from local storage:", this.monitoredStocks);
        this.cleanStockAlerts();
      }
    },
    cleanStockAlerts() {
      const now = Date.now();
      
      // 遍历所有监控股票
      this.monitoredStocks.forEach(stock => {
        // 遍历每个scale的alerts
        for (const scale in stock.alerts) {
          if (stock.alerts.hasOwnProperty(scale)) {
            // 根据scale确定时间阈值
            let timeThreshold;
            switch (scale) {
              case '1': // 1分钟周期，保留10分钟内数据
                timeThreshold = now - 10 * 60 * 1000;
                break;
              case '5': // 5分钟周期，保留30分钟内数据
                timeThreshold = now - 30 * 60 * 1000;
                break;
              case '30': // 30分钟周期，保留2小时内数据
                timeThreshold = now - 2 * 60 * 60 * 1000;
                break;
              case 'd': // 日线周期，保留当天数据
                // 获取今天0点的时间戳
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                timeThreshold = today.getTime();
                break;
              default:
                // 对于其他未知scale，也保留30分钟内数据
                timeThreshold = now - 30 * 60 * 1000;
                break;
            }
            
            // 过滤掉超出时间范围的警报
            stock.alerts[scale] = stock.alerts[scale].filter(alert => alert.timestamp > timeThreshold);
          }
        }
      });
      
      // 保存更新后的数据到localStorage
      this.saveStocksToLocalStorage();
    },
    
    // 2a: 保存股票列表到 localStorage
    saveStocksToLocalStorage() {
      // 保存包含isPinned属性和alerts警报信息的股票数据
      const stockData = this.monitoredStocks.map(stock => ({ 
        code: stock.code, 
        alerts: stock.alerts,
        isPinned: stock.isPinned || false 
      }));
      localStorage.setItem('monitoredStocks', JSON.stringify(stockData));
    },
    // 2b: 开始轮询
    startPolling() {
      // 先清除现有的所有定时器
      if (this.pollingIntervals && this.pollingIntervals.length > 0) {
        this.pollingIntervals.forEach(interval => {
          clearInterval(interval);
        });
      }
      
      // 重置定时器数组
      this.pollingIntervals = [];
      
      // 立即执行一次
      this.fetchAllData();

      // 实时数据单独有一个定时器，每10秒轮询一次
      this.pollingIntervals.push(setInterval(() => {
        this.fetchInstantData();
      }, 10 * 1000));
      
      // 根据设置创建定时器，只对1、5、30分钟周期创建定时器（d、w周期不需要定时器）
      // this.settings.selectedPeriods.forEach(period => {
      //   switch (period) {
      //     case '1':
      //       // 30秒轮询 (30,000毫秒)
      //       this.pollingIntervals.push(setInterval(() => {
      //         this.fetchDataForStocks(period);
      //       }, 10 * 1000));
      //       break;
      //     case '5':
      //       // 2.5分钟轮询 (150,000毫秒)
      //       this.pollingIntervals.push(setInterval(() => {
      //         this.fetchDataForStocks(period);
      //       }, 1 * 60 * 1000));
      //       break;
      //     case '30':
      //       // 15分钟轮询 (900,000毫秒)
      //       this.pollingIntervals.push(setInterval(() => {
      //         this.fetchDataForStocks(period);
      //       }, 5 * 60 * 1000));
      //       break;
      //     case 'd':
      //     // case 'w':
      //       this.pollingIntervals.push(setInterval(() => {
      //         if (this.settings.selectedPeriods.includes('d')) {
      //           this.fetchDataForStocks('d');
      //         }
      //         if (this.settings.selectedPeriods.includes('w')) {
      //           this.fetchDataForStocks('w');
      //         }
      //       }, 60 * 60 * 1000));    // 60分钟更新一次
      //       break;
      //     default:
      //       break;
      //   }
      // });
      
      // 添加定时清理过期警报的定时器，每5分钟执行一次
      this.pollingIntervals.push(setInterval(() => {
        this.cleanStockAlerts();
      }, 5 * 60 * 1000));
    },
    updateStockScoreUp(stockData, score, debug=false) {
      const lastScore = stockData.lastScore || -1;
      const newScore = stockData.score || -1;
      if (debug) {
        console.log('updateStockScoreUp: lastScore', lastScore, 'newScore', newScore, 'lastUp', stockData.lastScoreUp, 'newUp', stockData.scoreUp);
      }
      if (lastScore == -1) {
        // 首次获取到数据
        // stockData.lastScore = oldScore;
        // stockData.score = score;
        stockData.lastScoreUp = 0;
        stockData.scoreUp = 0;
      }
      else if (newScore != lastScore) {
        // 正常更新数据：分数有变化才处理
        stockData.lastScoreUp = stockData.scoreUp;
        stockData.scoreUp = newScore > lastScore ? 1 : -1;
        // stockData.lastScore = oldScore;
        // stockData.score = score;
      }
      if (debug) {
        console.log('updateStockScoreUp result: lastScoreUp', stockData.lastScoreUp, 'scoreUp', stockData.scoreUp);
      }
    },
    updateStockScaleScore(stock, scale) {
      let score = 0;
      // 首先判断this.settings.selectedPeriods是否包含输入参数scale
      if (!this.settings.selectedPeriods.includes(scale)) {
        return stock; // 或者返回适当的默认值
      }
      if (stock.indicatorData && stock.indicatorData[scale] && stock.indicatorData[scale].indicators2) {
        const indicator = stock.indicatorData[scale].indicators2;
        if (indicator.k.positive > 0) score++;
        if (indicator.k.up > 0) score++;
        if (indicator.macd.positive > 0) score++;
        if (indicator.macd.up > 0) score++;
        if (indicator.kdj.positive > 0) score++;
        if (indicator.kdj.up > 0) score++;
        if (indicator.ma5.positive > 0) score++;
        if (indicator.ma5.up > 0) score++;
        if (indicator.ma20.positive > 0) score++;
        if (indicator.ma20.up > 0) score++;
        if (indicator.ma60.positive > 0) score++;
        if (indicator.ma60.up > 0) score++;

        if (!stock.scaleScores) {
          stock.scaleScores = {};
        }
        if (!stock.scaleScores[scale]) {
          stock.scaleScores[scale] = {};
        }
        const oldScaleScore = stock.scaleScores[scale].score || -1;
        stock.scaleScores[scale].lastScore = oldScaleScore;
        stock.scaleScores[scale].score = score;
        // console.log('updateStockScaleScore: ', stock, scale, 'from ' + oldScaleScore + ' to ' + score);
        this.updateStockScoreUp(stock.scaleScores[scale], score);
      }
      return stock;
    },
    updateStockScore(stock) {
      let score = 0;
      let total = 0;
      const coefficients = this.settings.scoreCoefficients;
      if (!coefficients) {
        return stock;
      }
      for (const period of this.settings.selectedPeriods) {
        this.updateStockScaleScore(stock, period);
        if (stock.scaleScores) {
          const scaleScore = stock.scaleScores[period].score || 0;
          score += scaleScore * coefficients[period];
        }
        total += (12 * coefficients[period]);
      }
      
      // 计算百分比并取整
      const oldScore = stock.score || -1;
      const newScore = Math.round((score * 100) / total);
      // console.log("update stock score, score: " + score + ", total: " + total + ", newScore: " + newScore);
      // console.log('updateStockScore: ', stock.code, 'from ' + oldScore + ' to ' + newScore);
      // 更新股票的lastScore和score
      stock.lastScore = oldScore;
      stock.score = newScore;
      this.updateStockScoreUp(stock, newScore);
    },
    updateStockIndicators(stockIndex, stock, indicators) {
      if (!stock.indicatorData) {
        stock.indicatorData = {};
      }
      for (const [scale, stockData] of Object.entries(indicators)) {
        stock.indicatorData[scale] = stockData.indicators;
        stock.indicatorData[scale].indicators2 = stockData.indicators2;
      }
      this.$set(this.monitoredStocks, stockIndex, stock);
      for (const [scale, stockData] of Object.entries(indicators)) {
        // 处理股票数据并检查警报
        this.handleStockData(stockIndex, scale);
      }
    },
    async fetchInstantData() {
      const stockCodes = this.monitoredStocks.map(s => s.code);
      if (stockCodes.length === 0) return;
      const stockCodesParam = stockCodes.join(',');
      const results = await mockApi.getMultiStockData(stockCodesParam);
      try {
        // 处理批量获取的实时数据
        if (results.success) {
          // 遍历返回的数据并更新对应的股票
          for (const [key, stockFull] of Object.entries(results.data)) {
            const stockData = stockFull.quote;
            // 找到对应股票在数组中的索引
            const stockIndex = this.monitoredStocks.findIndex(s => s.code === key);
            if (stockIndex !== -1) {
              // 使用 Vue.set 更新数组中特定索引的元素，确保响应式更新
              const updatedStock = { ...this.monitoredStocks[stockIndex] };

              // 构造符合前端格式的数据
              const formattedData = {
                success: true,
                data: {
                  name: stockData.name,
                  price: stockData.currentPrice,
                  change: stockData.currentPrice - stockData.openingPrice,
                  todayChangePercent: (((stockData.currentPrice - stockData.openingPrice) / stockData.openingPrice) * 100).toFixed(2) + '%',
                  changePercent: (((stockData.currentPrice - stockData.closingPrice) / stockData.closingPrice) * 100).toFixed(2) + '%',
                  openingPrice: stockData.openingPrice,
                  highestPrice: stockData.highestPrice,
                  lowestPrice: stockData.lowestPrice,
                  closeingPrice: stockData.closingPrice,
                  dateChange: stockData.openingPrice - stockData.closingPrice,
                  lastPrice: -1,
                  lastScore: -1,
                  score: -1,
                }
              };
              updatedStock.currentData = formattedData;

              this.updateStockIndicators(stockIndex, updatedStock, stockFull.indicators);
              
              // console.log('stock data: ', key, stockData, formattedData);
              // 初始化indicatorData对象（如果不存在）
              let oldLastscore = -1;
              let oldScore = -1;
              // console.log("Update current data: ", updatedStock.currentData, formattedData);
              // 已经保证了每个stock都有currentData数据（有可能是初始化数据，但肯定有）
              formattedData.data.lastPrice = updatedStock.currentData.data.price;
              oldScore = updatedStock.currentData.data.score;
              oldLastscore = updatedStock.currentData.data.lastScore;
              const newScore = this.updateStockScore(updatedStock);
              // console.log("got instant data, oldScore: ", oldScore, "new Score: ", newScore);
              if (newScore != oldScore) {
                formattedData.data.lastScore = oldScore;
                formattedData.data.score = newScore;
              }
              else {
                formattedData.data.lastScore = oldLastscore;
                formattedData.data.score = oldScore;
              }
              updatedStock.currentData = formattedData;
              this.$set(this.monitoredStocks, stockIndex, updatedStock);
              // console.log("Update stock: ", updatedStock);
              // console.log(`更新股票${key}的实时数据:`, stockData.name);
            }
          }
        } else {
          console.log('批量获取实时数据失败:', results);
        }
      } catch (error) {
        console.error("获取实时股票数据失败:", error);
      }
      return results;
    },
    async fetchIndicatorData(stockCode, scale) {
      // 获取指定股票的技术指标数据
      const results = await mockApi.getIndicatorsData(stockCode, scale);
      try {
        // 处理获取到的指标数据
        if (results.success) {
          // 找到对应股票在数组中的索引
          const stockIndex = this.monitoredStocks.findIndex(s => s.code === stockCode);
          if (stockIndex !== -1) {
            // 更新股票数据
            const stock = this.monitoredStocks[stockIndex];
            const updatedStock = { ...stock };
            
            // 初始化indicatorData对象（如果不存在）
            if (!updatedStock.indicatorData) {
              updatedStock.indicatorData = {};
            }
            
            // 根据scale设置对应的数据
            updatedStock.indicatorData[scale] = results;
            
            // 使用 Vue.set 更新数组中特定索引的元素，确保响应式更新
            this.$set(this.monitoredStocks, stockIndex, updatedStock);
            
            // 处理股票数据并检查警报
            this.handleStockDataOld(stockIndex, scale);
          } else {
            console.log(`未找到股票代码${stockCode}在monitoredStocks中的索引`);
          }
        } else {
          // console.log(`获取股票${stockCode}的${scale}分钟指标数据失败:`, results);
        }
      } catch (error) {
        console.error(`获取股票${stockCode}的${scale}分钟指标数据时发生错误:`, error);
      }
    },
    async fetchMultiIndicatorData(scale) {
      const stockCodes = this.monitoredStocks.map(s => s.code);
      if (stockCodes.length === 0) return;
      const stockCodesParam = stockCodes.join(',');
      // 获取多股票的技术指标数据
      const results = await mockApi.getMultiIndicatorsData(stockCodesParam, scale);
      try {
        // 处理获取到的指标数据
        if (results.success) {
          // 遍历返回的数据并更新对应的股票
          for (const [key, stockData] of Object.entries(results.data)) {
            // 找到对应股票在数组中的索引
            const stockIndex = this.monitoredStocks.findIndex(s => s.code === key);
            if (stockIndex !== -1) {
              // 更新股票数据
              const stock = this.monitoredStocks[stockIndex];
              const updatedStock = { ...stock };
              
              // 初始化indicatorData对象（如果不存在）
              if (!updatedStock.indicatorData) {
                updatedStock.indicatorData = {};
              }
              
              // 根据scale设置对应的数据
              updatedStock.indicatorData[scale] = stockData;
              // console.log(`更新股票${key}的${scale}分钟指标数据:`, stockData, updatedStock);
              
              // 使用 Vue.set 更新数组中特定索引的元素，确保响应式更新
              this.$set(this.monitoredStocks, stockIndex, updatedStock);
              
              // 处理股票数据并检查警报
              this.handleStockData(stockIndex, scale);
            }
          }
        }
      } catch (error) {
        console.error(`获取股票${stockCodesParam}的${scale}分钟指标数据时发生错误:`, error);
      }
    },
    // 2b: 获取所有监控股票的数据
    // 支持日线和周线，因此scale是字符串，取值范围：“1”， “5”， “30”， “d”， “w”
    async fetchDataForStocks(scale) {
      const stockCodesToFetch = this.monitoredStocks.map(s => s.code);
      if (stockCodesToFetch.length === 0) return;
      
      // // 循环处理每个股票的技术指标数据
      // for (const code of stockCodesToFetch) {
      //   console.log(`开始获取股票${code}的${scale}分钟指标数据`);
      //   await this.fetchIndicatorData(code, scale);
      //   console.log(`股票${code}的${scale}分钟指标数据获取完成`);
      // }
      if (scale == "1" && getMarketParam() == 'sina') return;
      await this.fetchMultiIndicatorData(scale);
    },
    async fetchAllData() {
      // 根据设置确定要请求的数据周期
      const periodsToFetch = this.settings.selectedPeriods;
      await this.fetchInstantData();
      
      // 为每个选中的周期获取数据
      // for (const period of periodsToFetch) {
      //   await this.fetchDataForStocks(period);
      // }
    },
    // 清理超过半小时的警报记录
    cleanupExpiredAlerts() {
      const count = this.monitoredStocks.length;
      console.log('清理过期警报, 一共'+count+'只股票');
      const thirtyMinutesAgo = Date.now() - 61 * 60 * 1000;
      
      this.monitoredStocks.forEach(stock => {
        // 遍历每个时间周期的警报
        for (const scale in stock.alerts) {
          if (stock.alerts.hasOwnProperty(scale)) {
            // 过滤掉30分钟前的警报
            stock.alerts[scale] = stock.alerts[scale].filter(alert => alert.time < thirtyMinutesAgo);
          }
        }
      });
      
      // 保存更新后的数据到localStorage
      console.log('清理过期警报, 一共'+count+'只股票, 清理完成', this.monitoredStocks);
      this.saveStocksToLocalStorage();
    },
    
    getValueColor(changePercent, base = 0) {
      const value = parseFloat(changePercent);
      if (value > base) {
        return 'stock-color-red';
      } else if (value < base) {
        return 'stock-color-green';
      } else {
        return 'stock-color-grey';
      }
    },
    getArrowIcon(arrowValue, equal = '=') {
      // console.log('getArrow: ', macd);
      if (arrowValue > 0) {
        return '↑';
      }
      else if (arrowValue < 0) {
        return '↓';
      }
      return equal;
    },
    getCurrentPriceArrowIcon(currPrice, lastPrice) {
      if (lastPrice == 0 || lastPrice == -1) return '';
      return this.getArrowIcon(currPrice - lastPrice, '');
    },
    getKdjArrow(lastD, currD, equal = '=') {
      if (lastD < currD) {
        return '↑';
      }
      else if (lastD > currD) {
        return '↓';
      }
      return equal;
    },
    getCurrentPrice(code, price) {
      // 去掉股票代码前面两个字符
      const processedCode = code.substring(2);
      const precision = getPricePrecision(processedCode);
      // console.log('getCurrentPrice: ', code, price, precision);
      return parseFloat(price).toFixed(precision);
    },
    getDoubleArrowIcon(stockData) {
      // console.log('getDoubleArrowIcon: last arrow', stockData.lastScoreUp, 'current arrow', stockData.scoreUp);
      let arrow = '';
      if (stockData.scoreUp > 0) {
        arrow = '↑';
        if (stockData.lastScoreUp > 0) {
          arrow += '↑';
        }
      }
      else if (stockData.scoreUp < 0) {
        arrow = '↓';
        if (stockData.lastScoreUp < 0) {
          arrow += '↓';
        }
      }
      return arrow;
    },

    checkAlertTime(stockIndex, scale, alertName, time) {
      let alertTimes = (this.monitoredStocks[stockIndex].alertTimes && this.monitoredStocks[stockIndex].alertTimes[scale])
                 ? this.monitoredStocks[stockIndex].alertTimes[scale]
                 : {
                      macdRed: '', 
                      kdjGoldenCross: '',
                      kdjDeathCross: '',
                      turn6: '',
                      turn7: '',
                      turn8: '',
                      turn9: ''
                    };
      let needAlert = false;
      // console.log("checkAlertTime: ", stockIndex, scale, alertName, time, alertTimes);
      if (alertTimes[alertName] != time) {
        alertTimes[alertName] = time;
        if (!this.monitoredStocks[stockIndex].alertTimes) {
          this.monitoredStocks[stockIndex].alertTimes = [];
        }
        this.monitoredStocks[stockIndex].alertTimes[scale] = alertTimes;
        needAlert = true;
      }
      return needAlert;
    },

    handleStockData(stockIndex, scale) {
      const code = this.monitoredStocks[stockIndex].code;

      const newAlerts = this.processDataAndCheckAlerts(stockIndex, scale);

      // 2d: 将报警事件 POST 到后台
      if (newAlerts.length > 0) {
        // 显示浮动报警信息，设置通知显示时间为10秒
        newAlerts.forEach(alert => {
          this.showFloatingAlert(`${alert.code}: ${alert.event}(${alert.time})`, 'info', 10000);
        });
        
        // 服务端报警功能异常，先不发了
        // mockApi.postAlerts(newAlerts);
        
        // 2e: 更新页面显示的报警事件
        const targetStock = this.monitoredStocks.find(s => s.code === code);
        if (targetStock) {

          // 去重逻辑：比较新警报和原有警报，如果time和type都相同，则去掉原有警报
          newAlerts.forEach(newAlert => {
            targetStock.alerts[scale] = targetStock.alerts[scale].filter(alert => 
              !(alert.time === newAlert.time && alert.type === newAlert.type)
            );
          });
          
          // 按scale分组保存警报
          targetStock.alerts[scale].unshift(...newAlerts); // 将新警报加到对应scale数组的开头
          // 过滤掉30分钟前的警报
          const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
          targetStock.alerts[scale] = targetStock.alerts[scale].filter(alert => alert.timestamp > thirtyMinutesAgo);
        }
      }
    },
    processDataAndCheckAlerts(stockIndex, scale) {
      if (!this.monitoredStocks[stockIndex]
       || !this.monitoredStocks[stockIndex].currentData
       || !this.monitoredStocks[stockIndex].indicatorData
       || !this.monitoredStocks[stockIndex].indicatorData[scale]) {
        return [];
      }
      const code = this.monitoredStocks[stockIndex].code;
      const stockName = this.monitoredStocks[stockIndex].currentData ? this.monitoredStocks[stockIndex].currentData.data.name : '';
      const indicatorData = this.monitoredStocks[stockIndex].indicatorData[scale];
      // console.log("processDataAndCheckAlerts: ", stockIndex, scale, this.monitoredStocks[stockIndex], indicatorData);
      const indicatorTime = indicatorData.time;
      const finishedIndicatorTime = indicatorData.finishedTime;
      const score = this.monitoredStocks[stockIndex].score;
      const alerts = [];
      // console.log("processDataAndCheckAlerts: ", this.monitoredStocks[stockIndex]);
      
      // 检查当前周期是否在设置中启用报警
      const periodAlerts = this.settings.alerts[scale] || [];
      
      // 如果当前周期没有启用任何报警项，则直接返回空数组
      if (periodAlerts.length === 0) {
        return alerts;
      }
      
      const scaleInfo = scale == 'w' ? '周线' : (scale == 'd' ? '日线' : `${scale}F`);
      if (!indicatorTime || !finishedIndicatorTime) {
        return [];
      }
      const timePart = indicatorTime.split(' ')[1];
      const abbrTime = timePart ? timePart.slice(0, 5) : '';
      const finishedTimePart = finishedIndicatorTime.split(' ')[1];
      const finishedAbbrTime = finishedTimePart ? finishedTimePart.slice(0, 5) : '';
      // MACD红柱检测 - 仅在设置中启用时处理
      if (periodAlerts.includes('macdRed')) {
        if (indicatorData.greenToRed && this.checkAlertTime(stockIndex, scale, 'macdRed', indicatorTime)) {
          console.log("macdRed: ", indicatorData);
          alerts.push({
            code: code,
            event: `(${stockName}) ${scaleInfo} MACD 变红柱`,
            abbr: `红柱(${finishedAbbrTime})`,
            type: 'macdRed',
            color: 'stock-color-red',
            time: finishedIndicatorTime,
            timestamp: Date.now()
          });
        }
      }

      // KDJ报警 - 仅在设置中启用时处理
      if (indicatorData.kdj) {
        const kdj = indicatorData.kdj;
        if (periodAlerts.includes('kdjBullishDivergence') && kdj.isGoldenCross && this.checkAlertTime(stockIndex, scale, 'kdjBullishDivergence', indicatorTime)) {
          console.log(code, "kdj 金叉报警: ", kdj, this.monitoredStocks[stockIndex].currentData, "price: ", this.monitoredStocks[stockIndex].currentData.data.price);
          const shouldAlert = this.checkAndCompareDivergence(
            code, 
            scale, 
            'bullish',
            finishedIndicatorTime, 
            kdj.k, 
            kdj.d, 
            kdj.j, 
            this.monitoredStocks[stockIndex].currentData.data.price
          );
          
          // 如果满足条件，则添加报警
          if (shouldAlert) {
            alerts.push({
              code: code,
              event: `(${stockName}) ${scaleInfo} 底背`,
              abbr: `底背(${finishedAbbrTime})`,
              type: 'kdj',
              color: 'stock-color-red',
              time: finishedIndicatorTime,
              timestamp: Date.now()
            });
          }
        }
        if (periodAlerts.includes('kdjBearishDivergence') && kdj.isDeathCross && this.checkAlertTime(stockIndex, scale, 'kdjBearishDivergence', indicatorTime)) {
          console.log(code, "kdj 死叉报警: ", kdj);
          const shouldAlert = this.checkAndCompareDivergence(
            code, 
            scale, 
            'bearish',
            finishedIndicatorTime, 
            kdj.k, 
            kdj.d, 
            kdj.j, 
            this.monitoredStocks[stockIndex].currentData.data.price
          );
          
          // 如果满足条件，则添加报警
          if (shouldAlert) {
            alerts.push({
              code: code,
              event: `(${stockName}) ${scaleInfo} 顶背`,
              abbr: `顶背(${finishedAbbrTime})`,
              type: 'kdj',
              color: 'stock-color-green',
              time: finishedIndicatorTime,
              timestamp: Date.now()
            });
          }
        }
        if (periodAlerts.includes('kdjGoldenCross') && kdj.isGoldenCross && this.checkAlertTime(stockIndex, scale, 'kdjGoldenCross', indicatorTime)) {
          alerts.push({
            code: code,
            event: `(${stockName}) ${scaleInfo} KDJ金叉`,
            abbr: `金叉(${finishedAbbrTime})`,
            type: 'kdj',
            color: 'stock-color-red',
            time: finishedIndicatorTime,
            timestamp: Date.now()
          });
        }
        if (periodAlerts.includes('kdjDeathCross') && kdj.isDeathCross && this.checkAlertTime(stockIndex, scale, 'kdjDeathCross', indicatorTime)) {
          alerts.push({
            code: code,
            event: `(${stockName}) ${scaleInfo} KDJ死叉`,
            abbr: `死叉(${finishedAbbrTime})`,
            type: 'kdj',
            color: 'stock-color-green',
            time: finishedIndicatorTime,
            timestamp: Date.now()
          });
        }
      }
      
      // 神奇九转
      if (indicatorData.tdSequential) {
        const td = indicatorData.tdSequential;
        const currBuyTd = 'td' + td.buySetup;
        const currSellTd = 'td' + td.sellSetup;
        if (periodAlerts.includes(currBuyTd) || periodAlerts.includes(currSellTd)) {
          const tdName = periodAlerts.includes(currBuyTd) ? currBuyTd : currSellTd;
          if (this.checkAlertTime(stockIndex, scale, tdName, indicatorTime)) {
            const info = td.buySetup > 0 ? `底部${td.buySetup}转` : `顶部${td.sellSetup}转`;
            const abbrInfo = td.buySetup > 0 ? `底${td.buySetup}` : `顶${td.sellSetup}`;
            const color = td.buySetup > 0 ? 'stock-color-red' : 'stock-color-green';
            alerts.push({
              code: code,
              event: `(${stockName}) ${scaleInfo} ${info}`,
              abbr: `${abbrInfo}(${finishedAbbrTime})`,
              type: 'tdSequential',
              color: color,
              time: finishedIndicatorTime,
              timestamp: Date.now()
            });
          }
        }
      }

      // 分数报警
      const alertOverSell = true;
      const alertOverBuy = true;
      const isOverBuyWarning = this.overBuyWarning[code] || false;
      const isOverSellWarning = this.overSellWarning[code] || false;
      const overBuyThreshold = this.settings.thresholds ? this.settings.thresholds.overbought : 99;
      const overSellThreshold = this.settings.thresholds ? this.settings.thresholds.oversold : 1;
      if (alertOverBuy) {
        if (score >= overBuyThreshold) {
          if (!isOverBuyWarning) {
            alerts.push({
              code: code,
              event: `(${stockName}) 达到${score}分 超买`,
              abbr: `超买(${abbrTime})`,
              type: 'overBuy',
              color: 'stock-color-green',
              time: indicatorTime,
              timestamp: Date.now()
            });
          }
          this.overBuyWarning[code] = true;
        }
        else {
          this.overBuyWarning[code] = false;
        }
      }
      if (alertOverSell) {
        if (score <= overSellThreshold) {
          // console.log("overSell: 分数达到" + score + ", 正在报警？", this.overSellWarning);
          if (!isOverSellWarning) {
            alerts.push({
              code: code,
              event: `(${stockName}) 达到${score}分 超卖`,
              abbr: `超卖(${abbrTime})`,
              type: 'overSell',
              color: 'stock-color-red',
              time: indicatorTime,
              timestamp: Date.now()
            });
          }
          this.overSellWarning[code] = true;
        }
        else {
          // console.log("overSell: 分数达到" + score + " 恢复正常");
          this.overSellWarning[code] = false;
        }
      }
      return alerts;
    },
    
    // 请求浏览器通知权限
    requestNotificationPermission() {
      if ('Notification' in window) {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            console.log('浏览器通知权限已授予');
          } else {
            console.log('浏览器通知权限被拒绝');
          }
        }).catch(error => {
          console.error('请求通知权限时出错:', error);
        });
      } else {
        console.log('当前浏览器不支持通知功能');
      }
    },
    
    // 发送浏览器通知
    showBrowserNotification(title, message, timeout = 0) {
      // 检查浏览器是否支持通知
      if (!('Notification' in window)) {
        console.log('当前浏览器不支持通知功能');
        return;
      }
      
      // 检查通知权限
      if (Notification.permission === 'granted') {
        // 创建通知
        const notification = new Notification(title, {
          body: message,
          icon: '/favicon.ico', // 可选，通知图标
          timestamp: Date.now()
        });
        
        // 如果设置了超时时间，则在指定时间后关闭通知
        if (timeout > 0) {
          setTimeout(() => {
            notification.close();
          }, timeout);
        }
        
        // 可选：添加通知点击事件处理
        notification.onclick = function() {
          // 当用户点击通知时，可以执行一些操作
          window.focus(); // 聚焦到浏览器窗口
          this.close(); // 关闭通知
        };
      } else if (Notification.permission !== 'denied') {
        // 如果权限未被拒绝，请求权限
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            const notification = new Notification(title, {
              body: message,
              icon: '/favicon.ico',
              timestamp: Date.now()
            });
            
            // 如果设置了超时时间，则在指定时间后关闭通知
            if (timeout > 0) {
              setTimeout(() => {
                notification.close();
              }, timeout);
            }
            
            notification.onclick = function() {
              window.focus();
              this.close();
            };
          }
        });
      }
    },
    
    // 显示浮动报警信息
    showFloatingAlert(message, type = 'info', notificationTimeout = 0) {
      const alertId = ++this.alertIdCounter;
      const alert = {
        id: alertId,
        message: message,
        type: type,
        timestamp: Date.now()
      };
      
      // 添加到浮动报警数组
      this.floatingAlerts.push(alert);
      
      // 发送浏览器通知，支持设置显示时间
      this.showBrowserNotification('股票监控报警', message, notificationTimeout);
      
      // 1分钟后自动移除
      setTimeout(() => {
        this.removeFloatingAlert(alertId);
      }, 60000); // 1分钟 = 60000毫秒
    },
    
    // 移除浮动报警信息
    removeFloatingAlert(alertId) {
      const index = this.floatingAlerts.findIndex(alert => alert.id === alertId);
      if (index !== -1) {
        this.floatingAlerts.splice(index, 1);
      }
    },
    
    // 保存金叉信息到localStorage
    saveDivergenceData(stockCode, scale, type, time, k, d, j, price) {
      // 从localStorage获取现有数据
      let allData = {};
      const existingData = localStorage.getItem('divergence_data');
      if (existingData) {
        try {
          allData = JSON.parse(existingData);
        } catch (e) {
          console.error('解析KDJ交叉数据时出错:', e);
          allData = {};
        }
      }
      
      // 构建嵌套对象结构: stockCode -> scale -> 'gdc'
      if (!allData[stockCode]) {
        allData[stockCode] = {};
      }
      if (!allData[stockCode][scale]) {
        allData[stockCode][scale] = {};
      }
      
      if (allData[stockCode][scale][type]) {
        const key = 'old' + type;
        allData[stockCode][scale][key] = allData[stockCode][scale][type];
      }
      // 保存金叉数据
      allData[stockCode][scale][type] = {
        time: time,
        k: k,
        d: d,
        j: j,
        price: price
      };
      
      // 保存回localStorage
      localStorage.setItem('divergence_data', JSON.stringify(allData));
    },
    
    // 删除指定股票的背离数据
    removeDivergenceDataForStock(stockCode) {
      const existingData = localStorage.getItem('divergence_data');
      if (existingData) {
        try {
          const allData = JSON.parse(existingData);
          // 删除对应股票的数据
          if (allData[stockCode]) {
            delete allData[stockCode];
            // 保存回localStorage
            localStorage.setItem('divergence_data', JSON.stringify(allData));
          }
        } catch (e) {
          console.error('删除背离数据时出错:', e);
        }
      }
    },
    
    // 读取并判断金叉数据
    // type 可以是bullish 或者是 bearish
    checkAndCompareDivergence(stockCode, scale, type, time, k, d, j, price) {
      // 从localStorage获取所有数据
      let allData = {};
      const existingData = localStorage.getItem('divergence_data');
      if (existingData) {
        try {
          allData = JSON.parse(existingData);
        } catch (e) {
          console.error('解析背离数据时出错:', e);
          allData = {};
        }
      }
      
      // 检查是否存在对应股票和周期的数据
      const previousData = allData[stockCode] && allData[stockCode][scale] && allData[stockCode][scale][type];
      let isDivergence = false;

      if (previousData) {
        try {
          // 检查时间是否不同
          if (previousData.time !== time) {
            if (type == 'bullish') {
              // 检查条件：price2 < price1 且 d2 > d1
              isDivergence = price < previousData.price && d > previousData.d;
            }
            else {
              isDivergence = price > previousData.price && d < previousData.d;
            }            
          }
        } catch (error) {
          console.error('解析背离数据时出错:', error);
          isDivergence = false;
        }
      }
          
      // 用当前数据替换之前保存的数据
      this.saveDivergenceData(stockCode, scale, type, time, k, d, j, price);
      return isDivergence;
    },
    getPinBtnClass(stock) {
      return stock.isPinned ? 'pin-btn active' : 'pin-btn';
    } 
  },
  mounted() {
    // 页面首次挂载时初始化
    this.initMonitor();
    
    // 初始设置lastMonitoredStocksHash
    const stocks = localStorage.getItem('monitoredStocks');
    if (stocks) {
      this.lastMonitoredStocksHash = this.getMonitoredStocksHash(JSON.parse(stocks));
    }
    
    // 添加定时器，每5秒检查一次localStorage中的监控列表是否有更新
    this.localStorageUpdateInterval = setInterval(() => {
      this.checkAndLoadStocksFromLocalStorage();
    }, 5000);
  },

  beforeRouteEnter(to, from, next) {
    // 路由进入前记录来源路由
    next(vm => {
      vm.previousRoute = from.name || 'unknown';
    });
  },
  
  activated() {
    // 检查是否从设置页面返回，如果是则重新加载
    if (this.previousRoute === 'Settings') {
      this.loadSettings();
      this.startPolling();
    }
    // 对于其他页面切换，不执行任何操作（保持现有状态）
  },
  beforeDestroy() {
    // 组件销毁时停止所有轮询，防止内存泄漏
    if (this.pollingIntervals && this.pollingIntervals.length > 0) {
      this.pollingIntervals.forEach(interval => {
        clearInterval(interval);
      });
    }
    
    // 清理localStorage更新定时器
    if (this.localStorageUpdateInterval) {
      clearInterval(this.localStorageUpdateInterval);
    }
  }
};
</script>

<style scoped>
/* 移除.monitor-container样式，使用common-styles.css中的.page-container定义 */
/* 移除统一的.header样式，使用common-styles.css中的定义 */
/* .header { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  border-bottom: 2px solid #42b983; 
  padding-bottom: 10px;
  margin-bottom: 20px;
 }

 .header h2 {
  margin: 0;
  color: #2c3e50;
 } */
.add-stock-form { margin: 20px 0; display: flex; }
.add-stock-form input { flex-grow: 1; padding: 10px; margin-right: 10px; }
.add-stock-form button { padding: 10px 20px; }
.stock-list { margin-top: 30px; display: flex; flex-wrap: wrap; }
.stock-item { border: 1px solid #ddd; border-radius: 8px; padding: 15px; margin: 10px; width: calc(50% - 20px); box-sizing: border-box; }
.stock-title-container { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; position: relative; }
.stock-title-right { display: flex; align-items: center; }
.stock-title-left { display: flex; align-items: center; flex-wrap: wrap; }
.stock-title-container h3 { margin: 0 10px 0 0; }
.stock-info { font-size: 1em; margin-bottom: 0; }
.current-price { font-weight: bold; }
.delete-btn {
  background-color: #b9b6b7;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
  margin-left: 10px;
}
.delete-btn:hover {
  background-color: #ff7875;
}

.pin-btn {
  background-color: #d2d4d4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s;
}
.pin-btn:hover {
  background-color: #8c8d8d;
}
.pin-btn.active {
  background-color: #e9ebea;
}
.indicator-data { background-color: #f5f5f5; border-radius: 5px; margin-bottom: 4px; display: flex; align-items: center; justify-content: left; flex-wrap: nowrap; }
.indicator-title { width: 96px; flex-shrink: 0; }
.ma-container, .macd-container, .kdj-container, .kvalue-container { 
  display: flex; 
  flex-direction: column;
  justify-content: flex-start; 
  align-items: flex-start; 
  gap: 4px; 
  margin: 5px 4px; 
  width: 130px;
  flex-shrink: 0;
}
.ma-container {
  width: 120px;
}
.item-title {
  display: inline-block;
  width: 50px;
  text-align: left;
}
.ma-item, .kdj-item { margin-right: 0; }

.td-sequential-container {
  margin-top: 5px;
  font-size: 14px;
  flex-shrink: 0;
}
.alerts-container {
  flex: 1;
  min-width: 0;
  margin: 5px 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.alerts-container span {
  white-space: nowrap;
  font-size: small;
}

.td-buy {
  color: #ff4d4f;
  font-weight: bold;
}

.td-sell {
  color: #52c41a;
  font-weight: bold;
}

.td-item {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: #f0f0f0;
}
.empty-state { color: #888; text-align: center; padding: 20px; }
.alerts-list h4 { margin-top: 0; }
.alerts-list ul { padding-left: 20px; }
.alerts-list li { display: flex; justify-content: space-between; margin-bottom: 5px; }
.alert-time { color: #999; font-size: 0.9em; }

/* 股票涨跌幅颜色 */
.stock-color-red {
  color: #ff0000; /* 红色 */
}

.stock-color-grey {
  color: #808080; /* 灰色 */
}

.stock-color-green {
  color: #008000; /* 绿色 */
}

/* 浮动报警显示样式 */
.floating-alerts-container {
  position: fixed;
  top: 80px;  /* 调整为距离顶部80px，避开导航栏 */
  right: 20px;
  z-index: 1000;
  width: 300px;
  max-height: calc(100vh - 100px);  /* 限制最大高度 */
  overflow-y: auto;  /* 超出内容可滚动 */
}

.floating-alert {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 10px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  animation: slideIn 0.3s ease-out;
}

.floating-alert.info {
  border-left: 4px solid #1890ff;
}

.floating-alert.success {
  border-left: 4px solid #52c41a;
}

.floating-alert.warning {
  border-left: 4px solid #faad14;
}

.floating-alert.error {
  border-left: 4px solid #ff4d4f;
}

.alert-content {
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
}

.close-btn {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  padding: 0;
  margin-left: 8px;
}

.close-btn:hover {
  color: #333;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>